export const UNIQUE_ID_COOKIE_NAME = 'pleo-ab-unique-id'

export const SPLIT_TEST_COOKIE_NAME = 'pleo-split-test'
export const SPLIT_TEST_INTERNAL_COOKIE_NAME = 'pleo-split-test-internal'

export const SPLIT_TEST_PATHS_COOKIE_NAME = 'pleo-split-test-paths'
export const SPLIT_TEST_PATHS_INTERNAL_COOKIE_NAME = 'pleo-split-test-paths-internal'

export const SPLIT_TEST_ENCOUNTERED_COOKIE_NAME = 'pleo-encountered-split-tests'
export const SPLIT_TEST_ENCOUNTERED_INTERNAL_COOKIE_NAME = 'pleo-encountered-split-tests-internal'

export const FORWARDED_HOST_HEADER = 'X-Forwarded-Host'
// Set by the CloudFront viewer-request function from the `astro` KVS key's `version`. The
// origin-request function reads it to prefix Astro-routed requests with the deploy run ID, replacing
// the previously hardcoded STAGING/PROD run IDs. Absent => the run ID could not be resolved.
export const WEBSITE_RUN_ID_HEADER = 'X-Website-Run-Id'
export const SPLIT_TEST_EXPERIMENT_BRANCH_HEADER = 'X-Page-Split-Experiment-Branch'
export const FEATURE_BRANCH_HOST_SUFFIX = '.helios.staging.pleo.io'
export const PREVIEWS_BRANCH_HOST_SUFFIX = '.previews.staging.pleo.io'
export const FEATURE_BRANCH_BUCKET_DOMAIN =
    'pleo-commercial-helios-previews-staging.s3.amazonaws.com'
export const FEATURE_BRANCH_BUCKET_REGION = 'eu-west-1'

// The public hosts served by the unified Astro+Gatsby bucket. Requests on these exact hosts are
// URI-rewritten (never origin-switched) to either the Astro or the Gatsby prefix.
export const STAGING_HOST = 'www.staging.pleo.io'
export const PROD_HOST = 'www.pleo.io'

// The apex (non-www) hosts. Apex page requests are 301'd to www by handleWWWRedirect, but apex FILE
// requests (legal PDFs, /.well-known/*, favicon linked at the apex) fall through that redirect and
// must still resolve against the unified bucket's Gatsby prefix - otherwise they hit an unprefixed
// key and 403. See UNIFIED_HOSTS in direct-request-to-bucket.ts.
export const STAGING_APEX_HOST = 'staging.pleo.io'
export const PROD_APEX_HOST = 'pleo.io'

// Stable prefix under which the Gatsby build is published in the unified staging/prod buckets.
export const UNIFIED_GATSBY_PREFIX = '/gatsby'

// Astro's own sitemap, absent from migrated_routes. MUST match `filenameBase` in
// apps/website/astro.config.mjs - rename in both (and Gatsby's index link) or it 403s.
export const PAGES_SITEMAP = /^\/sitemap-pages-[\w-]+\.xml$/

// Routes hardcoded to Astro for preview hosts only. They are intentionally NOT in astro-routes.json's
// migrated_routes, so they never route to Astro on the unified staging/prod buckets - they exist so
// the Astro routing path stays exercisable per feature branch as pages are migrated.
//
// Locale sets mirror packages/data-layer/src/locale/locales.ts by urlCode (the edge bundle can't
// cheaply import data-layer). ALL_LOCALES is every supported locale; PRIMARY_LOCALES is the
// non-secondary subset. Keep both in sync with locales.ts.
//
// NOTE: the Astro build does not emit most of these pages yet, so they currently 404 on preview
// hosts. They are listed here so the routing flip is already in place for when that content lands.
// Matching is exact against the cleaned request URI (trailing slash stripped, before the index.html
// append), so the homepage of a locale is the bare "/<locale>" root.
const ALL_LOCALES = [
    'en',
    'da',
    'de',
    'es',
    'se',
    'ie',
    'fi',
    'at',
    'fr-be',
    'nl-be',
    'fr',
    'nl',
    'ee',
    'it',
    'pt',
    'lu',
    'no',
    'us',
]

const PRIMARY_LOCALES = ['en', 'da', 'de', 'es', 'se', 'ie', 'fr', 'nl']

// Secondary locales whose homepage (root, home-s, home-l) and /demo were migrated to Astro (reusing
// the EN copy). Mirrors secondaryHomepageUrlCodes / secondaryDemoUrlCodes in
// packages/data-layer/src/adapters/static/homepage/secondary.ts and namespaces/website.ts. The bare
// "/us" root is intentionally not previewed as a secondary locale - it is served to Astro in every
// environment via astro-routes.json.
const SECONDARY_LOCALES = ['fi', 'at', 'fr-be', 'nl-be', 'ee', 'it', 'pt', 'lu', 'no']

// Every locale now generates the home-s/home-l homepage decomposition (see
// packages/data-layer/src/namespaces/website.ts), so these two preview-only variants are added for
// ALL_LOCALES rather than per locale group. The third variant is the bare "/<locale>" root, which
// is added per group below (and, for "/us", served via astro-routes.json instead).
const HOMEPAGE_SEGMENT_PAGES = ['home-s', 'home-l']

// Secondary-locale segment pages served from Astro in previews, on top of the shared home-s/home-l
// variants above. NOTE: /demo is still 301-redirected to /ie/demo by redirects.json on every host,
// so it only becomes reachable in previews once that redirect is scoped to skip preview hosts.
const SECONDARY_LOCALE_PAGES = ['demo']

// Pricing pages are previewed in every locale; the rest only in primary locales.
const PRICING_PAGES = ['pricing', 'pricing-m', 'pricing-l']
// Primary-locale pages on top of the shared home-s/home-l variants above. 'demo' builds for all
// PRIMARY_LOCALES; never /us. Secondary-locale /demo pages are generated but stay redirect-gated to
// /ie/demo until the go-live cutover. The cards page's slug is localized per locale by
// localizePrimaryPageSlug below.
const PRIMARY_LOCALE_PAGES = ['business-expense-cards', 'demo', 'integrations/xero']

// The cards page is served from Astro in every primary locale, but German uses the localized slug
// /de/firmenkarte instead of business-expense-cards. Mirrors CARDS_DEFAULT_SLUG / cardsSlugOverrides
// in packages/data-layer/src/namespaces/website.ts - keep the default and overrides in sync with it.
const CARDS_DEFAULT_SLUG = 'business-expense-cards'
const CARDS_SLUG_OVERRIDES: Record<string, string> = {de: 'firmenkarte'}
const localizePrimaryPageSlug = (locale: string, page: string): string =>
    page === CARDS_DEFAULT_SLUG ? (CARDS_SLUG_OVERRIDES[locale] ?? page) : page

export const PREVIEW_ONLY_ASTRO_ROUTES = new Set([
    ...ALL_LOCALES.flatMap((locale) => PRICING_PAGES.map((page) => `/${locale}/${page}`)),
    ...ALL_LOCALES.flatMap((locale) => HOMEPAGE_SEGMENT_PAGES.map((page) => `/${locale}/${page}`)),
    ...PRIMARY_LOCALES.map((locale) => `/${locale}`),
    ...PRIMARY_LOCALES.flatMap((locale) =>
        PRIMARY_LOCALE_PAGES.map((page) => `/${locale}/${localizePrimaryPageSlug(locale, page)}`),
    ),
    ...SECONDARY_LOCALES.map((locale) => `/${locale}`),
    ...SECONDARY_LOCALES.flatMap((locale) =>
        SECONDARY_LOCALE_PAGES.map((page) => `/${locale}/${page}`),
    ),
    '/en/artificial-intelligence',
    '/da/artificial-intelligence',
    '/de/artificial-intelligence',
    '/es/artificial-intelligence',
    '/fr/artificial-intelligence',
    '/ie/artificial-intelligence',
    '/nl/artificial-intelligence',
    '/se/artificial-intelligence',
    '/us/artificial-intelligence',
    '/en/craft-of-finance',
    '/de/craft-of-finance',
    '/es/craft-of-finance',
    // "us" is not a PRIMARY_LOCALE, so its cards page is not covered by PRIMARY_LOCALE_PAGES above.
    '/us/business-expense-cards',
])

export const AWS_REGION = 'eu-west-1'

export const EXPERIMENTS_BUCKET_STAGING = 'pleo-commercial-helios-experiments-staging'
export const EXPERIMENTS_BUCKET_PROD = 'pleo-commercial-helios-experiments'
